import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, User, Bus, Calendar, DollarSign } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TransportStudentLedger = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTransportStudents();
  }, []);

  const fetchTransportStudents = async () => {
    try {
      const response = await apiClient.get('/api/transport/students');
      setStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const handleSearch = async () => {
    if (!selectedStudentId) {
      alert('Please select a student');
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.get(`/api/transport/reports/student-ledger?student_id=${selectedStudentId}`);
      if (response.data) {
        setLedger(response.data);
      } else {
        alert('Student not found in transport');
        setLedger(null);
      }
    } catch (error) {
      alert('Failed to fetch ledger');
      setLedger(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Search Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bus className="w-5 h-5 text-blue-600" />
            Transport Student Ledger
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label>Select Student *</Label>
              <Select value={selectedStudentId} onValueChange={setSelectedStudentId}>
                <SelectTrigger>
                  <SelectValue placeholder="-- Select Student --" />
                </SelectTrigger>
                <SelectContent>
                  {students.length === 0 ? (
                    <SelectItem value="__no_students__" disabled>No students enrolled in transport</SelectItem>
                  ) : (
                    students.map((student) => (
                      <SelectItem key={student.id} value={student.student_id}>
                        {student.student_name} - {student.route_name} ({student.status})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch} disabled={loading || !selectedStudentId}>
              <Search className="w-4 h-4 mr-2" />
              {loading ? 'Searching...' : 'View Ledger'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {ledger && (
        <>
          {/* Student Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-green-600" />
                Student Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Student Name</p>
                  <p className="font-medium">{ledger.student?.student_name || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Route</p>
                  <p className="font-medium">{ledger.student?.route_name || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Pickup Point</p>
                  <p className="font-medium">{ledger.student?.pickup_point || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Drop Point</p>
                  <p className="font-medium">{ledger.student?.drop_point || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Monthly Fee</p>
                  <p className="font-medium text-blue-600">₹{ledger.student?.monthly_fee || 0}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`px-2 py-1 text-xs rounded-full ${ledger.student?.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {ledger.student?.status || '-'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                Payment Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-600">Total Paid</p>
                  <p className="text-2xl font-bold text-green-700">₹{ledger.summary?.total_paid || 0}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600">Monthly Fee</p>
                  <p className="text-2xl font-bold text-blue-700">₹{ledger.summary?.monthly_fee || 0}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-600">Fine/Dues</p>
                  <p className="text-2xl font-bold text-red-700">₹{ledger.summary?.pending_fine || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment History Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Payment History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!ledger.payments || ledger.payments.length === 0 ? (
                <p className="text-center text-gray-500 p-4">No payments recorded.</p>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3">Payment #</th>
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Month/Year</th>
                      <th className="text-left p-3">Amount</th>
                      <th className="text-left p-3">Fine</th>
                      <th className="text-left p-3">Mode</th>
                      <th className="text-left p-3">Received By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.payments.map((payment, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{payment.payment_number}</td>
                        <td className="p-3">{payment.payment_date}</td>
                        <td className="p-3">{payment.month || '-'}/{payment.year}</td>
                        <td className="p-3 text-green-600">₹{payment.amount}</td>
                        <td className="p-3 text-red-600">₹{payment.fine_amount || 0}</td>
                        <td className="p-3">{payment.payment_mode}</td>
                        <td className="p-3">{payment.received_by || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default TransportStudentLedger;
